import * as shopifyCart from "@shopify/theme-cart";
import { types } from "./types";
import { addItemsToCart, updateItems } from "../../api/products";
import { handleOptimizeLineItemProperties } from "../../api/optimize";
import { addToCartEvent, addBundleToCartEvent } from "../../api/analytics";
import { partition } from "lodash-es";

function addMSRPProperty(getters, items) {
  return items.map((item) => {
    return {
      ...item,
      properties: {
        ...item.properties,
        _msrp: getMsrpForVariant(getters, item.id),
      },
    };
  });
}

function removeBundleProperties(properties) {
  delete properties._bundles;
  delete properties._rules_applied;
  delete properties._discounts;

  return properties;
}

function getMsrpForVariant(getters, variantId) {
  const price = getters["metafields/getVariantPrice"](variantId);
  const compareAtPrice =
    getters["metafields/getVariantCompareAtPrice"](variantId);
  return compareAtPrice || price;
}

export const actions = {
  async fetchCart({ commit, rootGetters }, ignoreSubscribe) {
    try {
      let cart = await shopifyCart.getState();
      const itemsWithoutMsrp = cart.items.filter(
        (item) => !item.properties._msrp
      );
      if (itemsWithoutMsrp.length) {
        await Promise.all(
          itemsWithoutMsrp.map(async (item) => {
            return await shopifyCart.updateItem(item.key, {
              quantity: item.quantity,
              properties: {
                ...item.properties,
                _msrp: getMsrpForVariant(rootGetters, item.variant_id),
                _created_at: Date.now(),
              },
            });
          })
        );
        cart = await shopifyCart.getState();
      }

      if (ignoreSubscribe) {
        commit(types.UPDATE_CART_IGNORE_SUBSCRIBE, cart);
      } else {
        commit(types.UPDATE_CART, cart);
      }
    } catch (error) {
      console.log(error);
    }
  },

  toggleCart({ commit, state }) {
    commit(types.TOGGLE_CART);

    if (!state.open) {
      commit(types.CLOSE_GIFT_FORM);
      commit(types.CLOSE_GIFT_WRAP);
    }
  },

  async updateCart({ commit }, { key, quantity, properties }) {
    try {
      await shopifyCart.updateItem(key, {
        quantity,
        properties: {
          ...removeBundleProperties(properties),
          _created_at: Date.now(),
        },
      });
      const cartState = await shopifyCart.getState();
      commit(types.UPDATE_CART, cartState);
    } catch (error) {
      console.log(error);
    }
  },

  toggleGiftForm({ commit, state }) {
    if (!state.giftFormOpen && state.giftWrapOpen) {
      commit(types.CLOSE_GIFT_FORM);
    }
    commit(types.TOGGLE_GIFT_FORM);
  },

  toggleGiftWrap({ commit, state }) {
    if (!state.giftWrapOpen && state.giftFormOpen) {
      commit(types.CLOSE_GIFT_WRAP);
    }

    commit(types.TOGGLE_GIFT_WRAP);
  },

  updateGiftWrap({ commit }, giftWrapItems) {
    commit(types.UPDATE_GIFT_WRAP, giftWrapItems);
  },

  async addToCart({ commit, state, rootGetters }, items) {
    try {
      const itemsToAdd = addMSRPProperty(
        rootGetters,
        handleOptimizeLineItemProperties([...items])
      );
      const existingItems = itemsToAdd.filter((item) =>
        state.data.items.find((cartItem) => cartItem.id === item.id)
      );
      const newItems = itemsToAdd.filter(
        (item) => !state.data.items.find((cartItem) => cartItem.id === item.id)
      );
      let addedItems;
      if (existingItems && existingItems.length) {
        const updates = existingItems.reduce((_updates, item) => {
          if (_updates[item.id]) {
            _updates[item.id] = _updates[item.id] + 1;
          } else {
            _updates[item.id] =
              state.data.items.find((cartItem) => cartItem.id === item.id)
                .quantity + (item.quantity || 1);
          }
          return _updates;
        }, {});
        const updatedCart = await updateItems(updates);
        addedItems = [
          ...updatedCart.items.filter((cartItem) =>
            existingItems.find((_item) => cartItem.id === _item.id)
          ),
        ];
      }

      if (newItems && newItems.length) {
        const { items } = await addItemsToCart(newItems);
        addedItems = items;
      }

      const cartState = await shopifyCart.getState();
      commit(types.UPDATE_CART, cartState);
      addToCartEvent(addedItems, cartState);
      return cartState;
    } catch (error) {
      console.log(error);
    }
  },

  async addBundleToCart(
    { commit, state, rootGetters },
    { items, bundleProduct }
  ) {
    try {
      const cartItems = state.data.items;
      const [existingItems, newItems] = partition(items, (item) => {
        return cartItems.filter((cartItem) => cartItem.id === item.id).length;
      });

      if (newItems && newItems.length) {
        await addItemsToCart(
          addMSRPProperty(
            rootGetters,
            handleOptimizeLineItemProperties(newItems)
          )
        );
      }

      if (existingItems && existingItems.length) {
        const itemsToUpdate = existingItems
          .map(
            (item) => cartItems.filter((cartItem) => cartItem.id === item.id)[0]
          )
          .reduce((items, item) => {
            items[item.id] = item.quantity + 1;
            return items;
          }, {});

        await updateItems(itemsToUpdate);
      }

      const cartState = await shopifyCart.getState();
      commit(types.UPDATE_CART, cartState);
      addBundleToCartEvent(bundleProduct, items, cartState);
    } catch (error) {
      console.log(error);
    }
  },

  // Removes a single quantity of an item in the cart using the change api
  async removeFromCart({ commit, state }, item) {
    try {
      if (!item) return;
      const { properties, key, quantity } = item;
      // get all items in the cart that are reactive to selected item
      const reactiveItems = getReactiveItems(item, state.data.items);
      const totalReactiveItemsQuantity = reactiveItems.reduce(
        (totalQuantity, _item) => totalQuantity + _item.quantity,
        0
      );
      if (reactiveItems && reactiveItems.length) {
        const reactiveItemsUpdate = reactiveItems.reduce(
          (updateMap, _reactiveItem) => {
            // get all items in the cart that the current reactive item is dependent on
            const itemsToReact = state.data.items.filter((_item) => {
              return _reactiveItem.properties._reactive.find(
                (_reactiveId) =>
                  _reactiveId === _item.id || _reactiveId === _item.product_id
              );
            });
            const itemsToReactQuantity = itemsToReact.reduce(
              (totalQuantity, _item) => totalQuantity + _item.quantity,
              0
            );
            // determine if reactive item should be decremented
            if (totalReactiveItemsQuantity >= itemsToReactQuantity) {
              updateMap[_reactiveItem.key] = _reactiveItem.quantity - 1 || 0;
            }
            return updateMap;
          },
          {}
        );

        await updateItems({
          [key]: quantity - 1 >= 0 ? quantity - 1 : 0,
          ...reactiveItemsUpdate,
        });

        const cartState = await shopifyCart.getState();
        commit(types.UPDATE_CART, cartState);
      } else {
        const cartState = await shopifyCart.updateItem(key, {
          quantity: quantity - 1 >= 0 ? quantity - 1 : 0,
          properties: removeBundleProperties(properties),
        });

        commit(types.UPDATE_CART, cartState);
      }
    } catch (error) {
      console.log(error);
    }
  },

  // Removes an entire line item in the cart using the change api
  async removeItem({ commit, state }, item) {
    try {
      if (!item) return;
      const { key } = item;
      const reactiveItems = getReactiveItems(item, state.data.items);
      const totalReactiveItemsQuantity = reactiveItems.reduce(
        (totalQuantity, _item) => totalQuantity + _item.quantity,
        0
      );
      if (reactiveItems && reactiveItems.length) {
        const reactiveItemsUpdate = reactiveItems.reduce(
          (updateMap, _reactiveItem) => {
            const itemsToReact = state.data.items.filter((_item) => {
              return (
                _item.id !== item.id &&
                _reactiveItem.properties._reactive.find(
                  (_reactiveId) =>
                    _reactiveId === _item.id || _reactiveId === _item.product_id
                )
              );
            });

            const itemsToReactQuantity = itemsToReact.reduce(
              (totalQuantity, _item) => totalQuantity + _item.quantity,
              0
            );
            if (totalReactiveItemsQuantity > itemsToReactQuantity) {
              const newQuantity =
                _reactiveItem.quantity - item.quantity >= 0
                  ? _reactiveItem.quantity - item.quantity
                  : 0;
              updateMap[_reactiveItem.key] = newQuantity;
            }
            return updateMap;
          },
          {}
        );
        await updateItems({
          [key]: 0,
          ...reactiveItemsUpdate,
        });

        const cartState = await shopifyCart.getState();
        commit(types.UPDATE_CART, cartState);
      } else {
        const cartState = await shopifyCart.removeItem(key);
        commit(types.UPDATE_CART, cartState);
      }
    } catch (error) {
      console.log(error);
    }
  },

  async clearNote({ commit }) {
    try {
      await shopifyCart.clearNote();
      commit(types.CLEAR_NOTE_SUCCESS);
    } catch (error) {
      console.log(error);
    }
  },

  async updateNote({ commit }, value) {
    try {
      commit(types.UPDATE_NOTE_SUCCESS, value);
      await shopifyCart.updateNote(value);
    } catch (error) {
      console.log(error);
    }
  },
};

export const getReactiveItems = (item, itemsToFilter) => {
  // get reactive items of an item based on current cart
  const reactiveItems = itemsToFilter.filter((_item) => {
    const _reactiveProp = _item.properties._reactive;
    if (!_reactiveProp) return false;
    return _reactiveProp.find(
      (_reactiveId) =>
        _reactiveId === item.id || _reactiveId === item.product_id
    );
  });
  return reactiveItems;
};
